import { useCallback, useState } from 'react';

import { useMixpanelTracking } from '../../../tracking/mixpanel/use-mixpanel-tracking';
import type { JobItemDetails } from '../../api/use-job-item-details';
import { useJsaTrackingContext } from './use-jsa-tracking-context';

type ItemType = 'job-level' | 'job-role';

type VersionInfo = {
    readonly itemId: string;
    readonly version: number;
};

type TrackVersionSavedParams = {
    readonly item: JobItemDetails;
    readonly itemId: string;
    readonly itemType: ItemType;
    readonly latestVersion: VersionInfo | undefined;
    readonly parentRoleId?: string;
};

type UseTrackVersionSavedOptions = {
    readonly architectureId: string;
};

type UseTrackVersionSavedResult = {
    readonly setIsNewVersionSave: (value: boolean) => void;
    readonly trackVersionSaved: (params: TrackVersionSavedParams) => void;
};

const getStatus = (item: JobItemDetails): 'draft' | 'in_review' | 'published' => {
    if (item.versionInfo?.isPublished === true) {
        return 'published';
    }
    if (item.versionInfo?.isReviewed === true) {
        return 'in_review';
    }

    return 'draft';
};

const getLevelId = (itemType: ItemType, itemId: string): string | undefined =>
    itemType === 'job-level' ? itemId : undefined;

const getRoleId = (itemType: ItemType, itemId: string, parentRoleId?: string): string | undefined =>
    itemType === 'job-role' ? itemId : parentRoleId;

export const useTrackVersionSaved = (options: UseTrackVersionSavedOptions): UseTrackVersionSavedResult => {
    const { architectureId } = options;
    const { trackEvent } = useMixpanelTracking();
    const trackingContext = useJsaTrackingContext();
    const [isNewVersionSave, setIsNewVersionSave] = useState(false);

    const trackVersionSaved = useCallback(
        ({ item, itemId, itemType, latestVersion, parentRoleId }: TrackVersionSavedParams): void => {
            trackEvent({
                name: 'job-role-version-saved',
                payload: {
                    ...trackingContext,
                    architectureId,
                    itemType,
                    levelId: getLevelId(itemType, itemId),
                    roleId: getRoleId(itemType, itemId, parentRoleId),
                    saveType: isNewVersionSave ? 'new_version' : 'update',
                    status: getStatus(item),
                    versionId: latestVersion?.itemId ?? itemId,
                    versionNumber: latestVersion?.version ?? 1,
                },
            });

            setIsNewVersionSave(false);
        },
        [trackEvent, trackingContext, isNewVersionSave, architectureId],
    );

    return {
        setIsNewVersionSave,
        trackVersionSaved,
    };
};
